import { BillingInfo, SharedData } from '@/@types';
import { removeMask } from '@/helpers/document';

import { router, useForm, usePage } from '@inertiajs/vue3';

import { secondStep, thirdStep } from '@/validations/checkout';

import { computed, InjectionKey, onBeforeMount, Ref, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { CartTotals } from './useCartTotals';
import { usePagSeguroEncryption } from './usePagseguroEncryption';
import { useZodValidation } from './useZodValidation';


export interface CheckoutStore {
  currentStep: Ref<number>
  agreed: Ref<boolean>
  controlStep: Ref<boolean>
  selectedPaymentMethod: Ref<string>
  handleSubmit: () => void
  nextStep: () => void
}

export const checkoutStoreKeys = Symbol('checkoutStore') as InjectionKey<CheckoutStore>

export function useCheckout({ current_step, agreed: agreedProps = false, billing_info, cartTotals, coupon, control_step }: { current_step: number, agreed?: boolean, billing_info?: BillingInfo, cartTotals?: CartTotals, coupon?: string, control_step?: boolean }) {
  const { user } = usePage<SharedData>().props
  const { locale } = useI18n()

  const checkControlStep = computed(() => {
    if (control_step) return control_step

    return current_step > 1 ? false : true
  })

  const currentStep = ref(current_step);
  const controlStep = ref(checkControlStep.value);
  const agreed = ref(agreedProps);
  const selectedPaymentMethod = ref('');

  const form = useForm({
    full_name: billing_info?.full_name || user?.name || '',
    ssn: billing_info?.ssn || '',
    email: billing_info?.email || user?.email,
    phone_number: billing_info?.phone_number || '',
    payment_type: '',
    zip_code: billing_info?.zip_code || '',
    ein: '',
    legal_name: '',
    encrypted_card: '',
    card_holder: '',
    card_number: '',
    card_exp: '',
    another_phone_number: billing_info?.another_phone_number || '',
    installments: '',
    card_ssn_ein: '',
    card_cvv: '',
    region_code: billing_info?.region_code || '',
    country: billing_info?.country || '',
    street: billing_info?.street || '',
    state: billing_info?.state || '',
    district: billing_info?.district || '',
    city: billing_info?.city || '',
    billing_type: 'individual',
    additional_info: billing_info?.additional_info || '',
    coupon_code: '',
    number: billing_info?.number || '',
    has_foreign_doc: billing_info?.has_foreign_doc as boolean,
  });

  const { validate } = useZodValidation(secondStep(locale.value), form)

  const validateThirdStep = ref<((args?: { clearErrors?: boolean }) => boolean)>(() => true)

  watch(
    () => locale.value,
    (newLocale) => {
      validateThirdStep.value = useZodValidation(thirdStep(newLocale), form).validate
    },
    { immediate: true }
  )

  function nextStep() {
    if (cartTotals && cartTotals.total.value <= 0) return currentStep.value = 4

    if (currentStep.value < 4) return currentStep.value++;
  }

  const { encryptCardData } = usePagSeguroEncryption(form)

  function handleSubmit() {
    form.clearErrors()

    if (currentStep.value === 1) {
      router.get(
        '/checkout',
        {},
        {
          preserveState: true,
        },
      )

      nextStep()

      if (cartTotals && cartTotals.total.value <= 0) return controlStep.value = true

      return form.payment_type ? controlStep.value = true : controlStep.value = false

    }

    if (currentStep.value === 2) {
      if (form.payment_type === 'credit_card') {
        encryptCardData()

        if (!validate({ clearErrors: false })) return

        return nextStep()
      } else if (form.payment_type !== 'credit_card') {
        form.encrypted_card = ''
        return nextStep()
      }
    }

    if (currentStep.value === 3) {
      if (!validateThirdStep.value()) return

      return nextStep()

    }

    if (currentStep.value === 4) {
      form.transform((data) => ({
        full_name: data.billing_type === 'individual' ? data.full_name : '',
        ssn: data.billing_type === 'individual' ? data.ssn : '',
        email: data.email,
        phone_number: data.phone_number,
        payment_type: data.payment_type,
        another_phone_number: data.another_phone_number,
        zip_code: data.zip_code,
        ein: data.billing_type === 'company' ? data.ein : '',
        installments: data.installments,
        card_holder: data.card_holder,
        card_ssn_ein: removeMask(data.card_ssn_ein),
        legal_name: data.billing_type === 'company' ? data.legal_name : '',
        encrypted_card: data.encrypted_card,
        country: data.country,
        street: data.street,
        state: data.state,
        district: data.district,
        city: data.city,
        region_code: data.region_code,
        additional_info: data.additional_info,
        billing_type: data.billing_type,
        number: data.number,
        coupon_code: data.coupon_code,
        has_foreign_doc: data.has_foreign_doc,
      })).post('/checkout/complete')
    }
  }

  watch(
    () => form.payment_type,
    () => {
      if (form.payment_type) {
        controlStep.value = true
      }
    },
  )

  watch(currentStep, (newVal) => {

    if (cartTotals && newVal === 1) {
      cartTotals.installmentTotals.value = 0
    }
  })

  watch(() => coupon, () => {
    if (coupon) {
      form.coupon_code = coupon
    }
  })


  watch(
    () => selectedPaymentMethod.value,
    () => {

      if (selectedPaymentMethod.value === 'company_billing') {
        form.billing_type = 'company'
      } else {
        form.billing_type = 'individual'
      }
    },
  )


  onBeforeMount(() => {
    if (currentStep.value > 1 && !agreed.value) {
      router.get('/cart')
    }
  })

  return {
    currentStep,
    agreed,
    form,
    controlStep,
    selectedPaymentMethod,
    handleSubmit,
    nextStep
  };
}
